import { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../components';
import BottomFixedOverlay from '../../../components/BottomFixedOverlay';
import { DispatchScheduleTimeGroup } from './DispatchScheduleTimeGroup';
import { getCutoffMinutes, getTimeInTimezone, WEEK_DAYS_ORDERED, durationToMinutes } from '../utils';

const T_PREFIX = 'lastmile_management.dispatch_schedules';

export const DispatchScheduleCalendarView = ({
    schedules,
    shippingMethods = [],
    warehouseTimeZone,
    canEdit = false,
    canCreate = false,
    onEditSchedule,
    onViewHistory,
    onToggleEnable,
    onDeleteSchedule,
    onCreateFromDay,
    onMoveSchedule,
    onDuplicateSchedule,
    onBulkEnable,
    onBulkDisable,
    onBulkDelete,
    isToggleLoading = false,
    isBulkLoading = false
}) => {
    const { i18n } = useTranslation();

    // Sort mode: 'dispatch_time' (default) or 'cutoff_time'
    const [sortBy, setSortBy] = useState('dispatch_time');

    // Selection state for bulk actions
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Stable key based on schedule IDs — avoids clearing selection on React Query refetches
    // that return identical data with a new array reference
    const scheduleKey = useMemo(() => schedules.map(s => s.id).sort((a, b) => a - b).join(','), [schedules]);

    // Clear selection when schedules actually change (added/removed)
    useEffect(() => {
        setSelectedIds(new Set());
        setSelectionMode(false);
    }, [scheduleKey]);

    const handleToggleSelect = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedIds(new Set(schedules.map(s => s.id)));
    }, [schedules]);

    const handleDeselectAll = useCallback(() => {
        setSelectedIds(new Set());
        setSelectionMode(false);
    }, []);

    // Current time tracking for cutoff status (updates every minute)
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    // Use warehouse timezone for all time calculations
    const warehouseTime = getTimeInTimezone(now, warehouseTimeZone);
    const todayDow = warehouseTime.dayOfWeek;
    const warehouseTimeStr = `${String(warehouseTime.hours).padStart(2, '0')}:${String(warehouseTime.minutes).padStart(2, '0')}`;

    // Optimistic day overrides for drag-and-drop
    const [optimisticMoves, setOptimisticMoves] = useState({});
    // Set of schedule IDs currently being moved (waiting for backend)
    const [pendingMoveIds, setPendingMoveIds] = useState(new Set());

    // Stable key including dispatch_day — changes when backend reflects a move
    const scheduleDataKey = useMemo(
        () => schedules.map(s => `${s.id}:${s.dispatch_day}`).sort().join(','),
        [schedules]
    );

    // Clear optimistic moves and pending state when schedule data actually changes
    useEffect(() => {
        setOptimisticMoves({});
        setPendingMoveIds(new Set());
    }, [scheduleDataKey]);

    // Group schedules by day, then by dispatch_time + time_before_dispatch within each day
    const groupedByDay = useMemo(() => {
        const byDay = {};
        WEEK_DAYS_ORDERED.forEach(d => { byDay[d] = {}; });
        schedules.forEach(s => {
            const day = optimisticMoves[s.id] !== undefined ? optimisticMoves[s.id] : s.dispatch_day;
            if (!byDay[day]) byDay[day] = {};
            const timeKey = `${s.dispatch_time || '__no_time__'}|${s.time_before_dispatch || ''}`;
            if (!byDay[day][timeKey]) byDay[day][timeKey] = [];
            byDay[day][timeKey].push(s);
        });

        // Convert to sorted array of time groups per day
        const result = {};
        WEEK_DAYS_ORDERED.forEach(day => {
            result[day] = Object.entries(byDay[day])
                .sort(([, groupA], [, groupB]) => {
                    if (sortBy === 'cutoff_time') {
                        // Sort by cutoff time (dispatch_time - time_before_dispatch)
                        const minsA = getCutoffMinutes(groupA[0].dispatch_time || '', groupA[0].time_before_dispatch) ?? 0;
                        const minsB = getCutoffMinutes(groupB[0].dispatch_time || '', groupB[0].time_before_dispatch) ?? 0;
                        return minsA - minsB;
                    }
                    // Default: sort by dispatch_time, then by cutoff for same dispatch_time
                    const timeA = groupA[0].dispatch_time || '';
                    const timeB = groupB[0].dispatch_time || '';
                    const timeCmp = timeA.localeCompare(timeB);
                    if (timeCmp !== 0) return timeCmp;
                    return durationToMinutes(groupB[0].time_before_dispatch) - durationToMinutes(groupA[0].time_before_dispatch);
                })
                .map(([, group]) =>
                    [...group].sort((a, b) => (a.carrier_name || '').localeCompare(b.carrier_name || ''))
                );
        });
        return result;
    }, [schedules, optimisticMoves, sortBy]);

    // Drag and drop state
    const [dragOverDay, setDragOverDay] = useState(null);
    // Confirmation dialog state for drag-and-drop moves
    const [pendingDrop, setPendingDrop] = useState(null);

    const handleDragOver = (e, day) => {
        if (!canEdit || !onMoveSchedule) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverDay(day);
    };

    const handleDragLeave = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOverDay(null);
    };

    const executeMoveSchedules = (scheduleIds, day) => {
        // Optimistic update: immediately move cards visually
        const moves = {};
        scheduleIds.forEach(id => { moves[id] = day; });
        setOptimisticMoves(prev => ({ ...prev, ...moves }));
        // Mark as pending
        setPendingMoveIds(prev => {
            const next = new Set(prev);
            scheduleIds.forEach(id => next.add(id));
            return next;
        });
        // Trigger backend updates with callbacks
        scheduleIds.forEach(id => onMoveSchedule(id, day, {
            onSuccess: (sid) => setPendingMoveIds(prev => {
                const next = new Set(prev);
                next.delete(sid);
                return next;
            }),
            onError: (sid) => {
                // Revert optimistic move on failure
                setOptimisticMoves(prev => {
                    const next = { ...prev };
                    delete next[sid];
                    return next;
                });
                setPendingMoveIds(prev => {
                    const next = new Set(prev);
                    next.delete(sid);
                    return next;
                });
            }
        }));
    };

    const handleDrop = (e, day) => {
        e.preventDefault();
        setDragOverDay(null);
        try {
            const parsed = JSON.parse(e.dataTransfer.getData('application/x-dispatch-schedule'));
            const rawIds = parsed?.scheduleIds;
            if (!Array.isArray(rawIds)) return;
            // Validate IDs exist in current schedules and filter out those already in target day
            const validIds = new Set(schedules.map(s => s.id));
            const scheduleIds = rawIds
                .filter(id => typeof id === 'number' && validIds.has(id))
                .filter(id => {
                    const effectiveDay = optimisticMoves[id] !== undefined
                        ? optimisticMoves[id]
                        : schedules.find(s => s.id === id)?.dispatch_day;
                    return effectiveDay !== day;
                });
            if (scheduleIds.length && onMoveSchedule) {
                setPendingDrop({ scheduleIds, day });
            }
        } catch (err) {
            if (process.env.REACT_APP_ENVIRONMENT === 'DEVELOPMENT') console.warn('Invalid drop data:', err);
        }
    };

    const handleConfirmMove = () => {
        if (pendingDrop) {
            executeMoveSchedules(pendingDrop.scheduleIds, pendingDrop.day);
        }
        setPendingDrop(null);
    };

    const handleCancelMove = () => {
        setPendingDrop(null);
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            {/* Toolbar + day headers */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    {canEdit && (onBulkEnable || onBulkDisable || onBulkDelete) && (
                        <button
                            onClick={() => {
                                if (selectionMode) {
                                    setSelectedIds(new Set());
                                    setSelectionMode(false);
                                } else {
                                    setSelectionMode(true);
                                }
                            }}
                            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors border ${
                                selectionMode
                                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {selectionMode
                                ? i18n.t(`${T_PREFIX}.bulk.exit_selection`)
                                : i18n.t(`${T_PREFIX}.bulk.select`)
                            }
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="text-xs border border-gray-300 rounded pl-2 pr-6 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M3%205l3%203%203-3%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0.5rem_center]"
                    >
                        <option value="dispatch_time">{i18n.t(`${T_PREFIX}.sort.by_dispatch_time`)}</option>
                        <option value="cutoff_time">{i18n.t(`${T_PREFIX}.sort.by_cutoff_time`)}</option>
                    </select>
                    <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 font-medium px-2 py-0.5 rounded text-xs">
                        {i18n.t(`${T_PREFIX}.warehouse_time`)}: {warehouseTimeStr}
                        {warehouseTimeZone && <span className="text-indigo-400 font-normal">({warehouseTimeZone})</span>}
                    </span>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-2">
                {/* Day headers */}
                {WEEK_DAYS_ORDERED.map(day => {
                    const isToday = day === todayDow;
                    return (
                        <div key={`header-${day}`} className={`text-center pb-2 border-b ${isToday ? 'border-indigo-300' : 'border-gray-100'}`}>
                            <span
                                className={`text-xs font-semibold uppercase ${
                                    isToday
                                        ? `text-indigo-700 font-bold${canEdit && canCreate && onCreateFromDay ? ' cursor-pointer hover:underline' : ''}`
                                        : canEdit && canCreate && onCreateFromDay
                                            ? 'text-indigo-500 hover:text-indigo-700 cursor-pointer hover:underline'
                                            : 'text-gray-500'
                                }`}
                                onClick={() => canEdit && canCreate && onCreateFromDay?.(day)}
                                title={canEdit && canCreate && onCreateFromDay ? i18n.t(`${T_PREFIX}.add_schedule`) : undefined}
                            >
                                {i18n.t(`lastmile_management.days.${day}`)}
                            </span>
                        </div>
                    );
                })}

                {/* Day columns with slots */}
                {WEEK_DAYS_ORDERED.map(day => {
                    const isToday = day === todayDow;
                    return (
                        <div
                            key={`col-${day}`}
                            className={`flex flex-col gap-1.5 min-h-[4rem] pt-1 rounded-md px-0.5 transition-colors ${
                                dragOverDay === day
                                    ? 'bg-indigo-100 ring-2 ring-indigo-300 ring-inset'
                                    : ''
                            }`}
                            onDragOver={(e) => handleDragOver(e, day)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, day)}
                        >
                            {groupedByDay[day]?.length > 0 ? (
                                groupedByDay[day].map((timeGroup) => {
                                    const first = timeGroup[0];
                                    const key = `${first.dispatch_time}|${first.time_before_dispatch || ''}`;
                                    return (
                                        <DispatchScheduleTimeGroup
                                            key={`group-${key}`}
                                            dayLabel={i18n.t(`lastmile_management.days_short.${day}`)}
                                            schedules={timeGroup}
                                            shippingMethods={shippingMethods}
                                            canEdit={canEdit}
                                            onEditSchedule={onEditSchedule}
                                            onViewHistory={onViewHistory}
                                            onToggleEnable={onToggleEnable}
                                            onDeleteSchedule={onDeleteSchedule}
                                            onDuplicateSchedule={onDuplicateSchedule}
                                            isToggleLoading={isToggleLoading}
                                            pendingMoveIds={pendingMoveIds}
                                            selectionMode={selectionMode}
                                            selectedIds={selectedIds}
                                            onToggleSelect={handleToggleSelect}
                                        />
                                    );
                                })
                            ) : (
                                <div className="rounded-md border border-dashed border-gray-100 bg-gray-50 flex items-center justify-center min-h-[3rem]">
                                    <span className="text-gray-300 text-xs">—</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <ConfirmDialog
                open={!!pendingDrop}
                setOpen={(v) => { if (!v) setPendingDrop(null); }}
                title={i18n.t(`${T_PREFIX}.confirm_move.title`)}
                description={pendingDrop ? i18n.t(`${T_PREFIX}.confirm_move.description`, {
                    count: pendingDrop.scheduleIds.length,
                    day: i18n.t(`lastmile_management.days.${pendingDrop.day}`)
                }) : ''}
                confirmLabel={i18n.t(`${T_PREFIX}.confirm_move.confirm`)}
                cancelLabel={i18n.t(`${T_PREFIX}.confirm_move.cancel`)}
                onConfirm={handleConfirmMove}
                onCancel={handleCancelMove}
            />

            <BottomFixedOverlay centered visible={selectionMode} className="w-10/12">
                <div className="rounded-lg border border-blue-300 shadow-xl bg-gray-100 px-6 pt-4">
                    <div className="mb-4 flex justify-between">
                        <span className="font-bold text-base">
                            {i18n.t(`${T_PREFIX}.bulk.select_action`)}
                        </span>
                        <div className="flex gap-3">
                            <span
                                className="font-normal text-base cursor-pointer text-indigo-500 hover:text-indigo-400"
                                onClick={handleSelectAll}
                            >
                                {i18n.t(`${T_PREFIX}.bulk.select_all`)}
                            </span>
                            <span
                                className="font-normal text-base cursor-pointer text-blue-400 hover:text-blue-300"
                                onClick={handleDeselectAll}
                            >
                                {i18n.t(`${T_PREFIX}.bulk.deselect_all`)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mb-2">
                        {onBulkEnable && (
                            <button
                                className={`rounded-md bg-white border border-gray-300 shadow-sm px-4 py-2 outline-none text-sm font-medium ${isBulkLoading || selectedIds.size === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:bg-green-50'}`}
                                onClick={() => onBulkEnable([...selectedIds])}
                                disabled={isBulkLoading || selectedIds.size === 0}
                            >
                                {i18n.t(`${T_PREFIX}.actions.enable`)}
                            </button>
                        )}
                        {onBulkDisable && (
                            <button
                                className={`rounded-md bg-white border border-gray-300 shadow-sm px-4 py-2 outline-none text-sm font-medium ${isBulkLoading || selectedIds.size === 0 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                onClick={() => onBulkDisable([...selectedIds])}
                                disabled={isBulkLoading || selectedIds.size === 0}
                            >
                                {i18n.t(`${T_PREFIX}.actions.disable`)}
                            </button>
                        )}
                        {onBulkDelete && (
                            <button
                                className={`rounded-md bg-white border border-red-300 shadow-sm px-4 py-2 outline-none text-sm font-medium ${isBulkLoading || selectedIds.size === 0 ? 'text-red-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                                onClick={() => onBulkDelete([...selectedIds])}
                                disabled={isBulkLoading || selectedIds.size === 0}
                            >
                                {i18n.t(`${T_PREFIX}.actions.delete`)}
                            </button>
                        )}
                    </div>
                    <div className="border-t-2 pt-2 my-2">
                        <span className="font-normal text-gray-500 text-sm">
                            {selectedIds.size} {i18n.t(`${T_PREFIX}.bulk.schedules_selected`)}
                        </span>
                    </div>
                </div>
            </BottomFixedOverlay>
        </div>
    );
};
